import React, { useRef, useEffect } from 'react';
import {
  Input,
  InputGroup,
  Spinner,
  IconButton,
} from '@chakra-ui/react';
import { CloseIcon } from '@chakra-ui/icons';
import { Portal } from '@chakra-ui/react';
import { ReactComponent as NotificationIcon } from '../../../assets/images/icons/ic-notification.svg';
import { Box } from '../../../components/UI-CHAKRA/Box';
import { Flex } from '../../../components/UI-CHAKRA/Flex';
import { Text } from '../../../components/UI-CHAKRA/Text';
import { Button } from '../../../components/UI-CHAKRA/Button';
import { ConsentDialogUIProps } from './types';

export const ConsentDialogUI: React.FC<ConsentDialogUIProps> = ({
  isVisible,
  email,
  emailError,
  apiError,
  isSubmitting,
  onEmailChange,
  onAccept,
  onRefuse,
  onClose,
}) => {
  const emailInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isVisible && emailInputRef.current) {
      setTimeout(() => {
        emailInputRef.current?.focus();
      }, 100);
    }
  }, [isVisible]);

  if (!isVisible) return null;

  return (
    <Portal>
      <Box
        position="fixed"
        bottom={{ base: "10px", md: "20px" }}
        left={{ base: "10px", md: "20px" }}
        right={{ base: "10px", md: "auto" }}
        maxW={{ base: "calc(100vw - 20px)", md: "420px" }}
        w={{ base: "auto", md: "420px" }}
        bg="linear-gradient(135deg, #4C1D7C 0%, #5A189A 100%)"
        color="white"
        borderRadius="20px"
        boxShadow="0px 12px 40px rgba(8, 15, 52, 0.4)"
        zIndex={1400}
        p={0}
        animation="slideInLeft 0.3s ease-out"
        sx={{
          '@keyframes slideInLeft': {
            from: {
              transform: 'translateX(-100%)',
              opacity: 0,
            },
            to: {
              transform: 'translateX(0)',
              opacity: 1,
            },
          },
        }}
      >
        <Box position="relative" pb={3} pt={{ base: 5, md: 6 }} px={{ base: 4, md: 6 }}>
          <Text fontSize={{ base: "16px", md: "18px" }} fontWeight={700} color="white" lineHeight="1.2" mb={2}>
            Dúvidas sobre novas regras da ANVISA?
          </Text>
          <Text fontSize={{ base: "12px", md: "13px" }} maxW={'90%'} fontWeight={400} color="rgba(255, 255, 255, 0.9)" lineHeight="1.4">
            Ative e seja informado sobre mudanças regulatórias da ANVISA, dicas de uso da Mevo e outras atualizações importantes.          </Text>
        </Box>

        <Box px={{ base: 4, md: 6 }} py={0} maxW={'90%'}>
          <Flex direction="column" gap={4}>
            <InputGroup>
              <Input
                ref={emailInputRef}
                type="email"
                value={email}
                onChange={(e) => onEmailChange(e.target.value)}
                placeholder="Seu email"
                bg="rgba(255, 255, 255, 0.2)"
                color="white"
                borderColor="transparent"
                borderRadius="12px"
                h={{ base: "48px", md: "56px" }}
                fontSize={{ base: "14px", md: "16px" }}
                fontWeight={400}
                _placeholder={{ 
                  color: 'rgba(255, 255, 255, 0.7)',
                  fontSize: { base: "14px", md: "16px" }
                }}
                _hover={{ bg: 'rgba(255, 255, 255, 0.25)' }}
                _focus={{
                  bg: 'rgba(255, 255, 255, 0.3)',
                  borderColor: 'rgba(255, 255, 255, 0.5)',
                  boxShadow: '0 0 0 2px rgba(255, 255, 255, 0.2)',
                }}
                isDisabled={isSubmitting}
              />
            </InputGroup>

            {emailError && (
              <Text fontSize={{ base: "13px", md: "14px" }} color="#FFB4B4" mt={-2}>
                {emailError}
              </Text>
            )}

            {apiError && (
              <Box
                bg="rgba(192, 65, 65, 0.2)"
                borderRadius="8px"
                p={3}
                borderLeft="3px solid #C04141"
              >
                <Text fontSize={{ base: "13px", md: "14px" }} color="#FFB4B4">
                  {apiError}
                </Text>
              </Box>
            )}

            <Text fontSize={{ base: "11px", md: "12px" }} fontWeight={400} color="rgba(255, 255, 255, 0.8)" lineHeight="1.4">
              Não enviamos spam. Cancele quando quiser.
            </Text>
          </Flex>
        </Box>

        <Box px={{ base: 4, md: 6 }} pb={{ base: 4, md: 6 }} pt={4}>
          <Flex direction="row" gap={{ base: 2, md: 3 }} w="100%" align="center">
            <Button
              onClick={onRefuse}
              bg="transparent"
              color="white"
              fontWeight={600}
              fontSize={{ base: "12px", md: "14px" }}
              h={{ base: "44px", md: "48px" }}
              px={{ base: 4, md: 6 }}
              borderRadius="12px"
              _hover={{ bg: 'rgba(255, 255, 255, 0.1)' }}
              _active={{ bg: 'rgba(255, 255, 255, 0.2)' }}
              isDisabled={isSubmitting}
              flex="0 0 auto"
            >
              Agora não
            </Button>

            <Button
              onClick={onAccept}
              bg="#FFB6BF"
              color="#5A189A"
              fontWeight={700}
              fontSize={{ base: "12px", md: "14px" }}
              h={{ base: "44px", md: "48px" }}
              borderRadius="24px"
              flex="1"
              _hover={{ 
                bg: "#FFB6BF",
                transform: 'translateY(-1px)'
              }}
              _active={{ 
                bg: "#FFB6BF",
                transform: 'translateY(0px)'
              }}
              isDisabled={isSubmitting}
              rightIcon={isSubmitting ? <Spinner size="sm" color="#5A189A" /> : <NotificationIcon width="16px" height="16px" />}
              transition="all 0.2s ease"
            >
              {isSubmitting ? 'Processando...' : 'Ativar'}
            </Button>
          </Flex>
        </Box>
      </Box>
    </Portal>
  );
};

export default ConsentDialogUI;
